import {
  CSSProperties,
  FC, RefObject,
  useEffect,
  useRef,
  useState,
} from 'react'
import useTranslation from 'next-translate/useTranslation'
import { Button, Nav, Navbar } from 'rsuite'

import RightCarouselIcon from '../../Icons/RightCarouselIcon'
import LeftCarouselIcon from '../../Icons/LeftCarouselIcon'
import { getRefDimension } from '../../../utils/util'
import { ProductNavigationRefs } from '../../../utils/types/Product'
import { scrollToRef } from '../../../utils/scroll'
import { debounce } from '../../../utils/event-utils'
import { ScreenSize } from '../../../utils/constants'
import useActiveAttributePanel from '../hooks/useActiveAttributePanel'
import useIsUnderSelectedElement from '../../hooks/useIsUnderSelectedElement'
import { useHideHeaderOnScrollCondition, useHeaderRef } from '../../Layouts/Header/hooks/headerContext'
import useIsScrollingUp from '../../hooks/useIsScrollingUp'
import useMounted from '../../../services/useMounted'
import useIsOnMobile from '../../../services/useIsOnMobile'
import useHasWindowScrolledToTop from '../../../services/useHasWindowScrolledToTop'

import styles from '../../../styles/ProductNavigation.module.less'

interface ProductNavigationProps {
  navigationRefs: ProductNavigationRefs
}

const SEGMENT_MARGIN = 40 // Margin between segment content and top divider
const NAVBAR_TOP_MARGIN = 30

const ProductNavigation: FC<ProductNavigationProps> = ({ navigationRefs }) => {
  const {
    productFeaturesRef,
    productStandardsRef,
    deliveryTermsRef,
    supplierProductsRef,
  } = navigationRefs

  const { t } = useTranslation('products')
  const isScreenSm = useIsOnMobile(ScreenSize.sm)
  const isScreenMd = useIsOnMobile(ScreenSize.md)
  const isScreenLg = useIsOnMobile(ScreenSize.lg)

  const isMounted = useMounted()
  const mountedWindow = isMounted ? window : null // Needed to ensure window object exists
  const scrollPosition = mountedWindow?.scrollY || 0

  const navbarRef = useRef<HTMLDivElement>(null)
  const navbarScrollableRef = useRef<HTMLUListElement>(null)
  const navbarScrollableElement = navbarScrollableRef?.current
  const headerRef = useHeaderRef()

  const navbarHeight = getRefDimension('height', navbarRef)
  const navbarBottom = getRefDimension('bottom', navbarRef) + scrollPosition
  const headerHeight = getRefDimension('height', headerRef)
  const productFeaturesHeight = getRefDimension('height', productFeaturesRef)

  const [isLeftArrowVisible, setIsLeftArrowVisible] = useState(false)
  const [isRightArrowVisible, setIsRightArrowVisible] = useState(true)

  // Workaround to target product navbar bottom: Target productFeatures instead of productNavbar,
  // and use an offset.
  // Needed due to scroll behaviour --> position of product navbar changes on scroll,
  // and hence the trigger would otherwise become unpredictable and volatile.
  const isUnderProductNavOffset = productFeaturesHeight
  + headerHeight

  const scrollDownOffset = isScreenMd
    ? isUnderProductNavOffset
    : isUnderProductNavOffset + navbarHeight
  const scrollUpOffset = isUnderProductNavOffset + navbarHeight + NAVBAR_TOP_MARGIN

  const isUnderProductNav = useIsUnderSelectedElement({
    elementRef: productFeaturesRef,
    offset: {
      scrollDownOffset,
      scrollUpOffset,
    },
  })

  // Hide header when scrolling below product navbar with transition animation
  // Mobile: Above product navbar hide half of header, below product navbar hide full header
  // Desktop: Always hide full header
  const isScrollingUp = useIsScrollingUp()
  const isScrolledToTop = useHasWindowScrolledToTop()
  const MOBILE_DIVIDER_OFFSET = 4
  const customerTypePickerOffset = isScreenSm ? 30 : 0
  const hideAmountMobile = isScreenMd
    ? headerHeight / 2 + MOBILE_DIVIDER_OFFSET - customerTypePickerOffset
    : headerHeight
  const hideAmount = !isUnderProductNav ? hideAmountMobile : headerHeight
  // On desktop, only hide header when scrolled below product navbar
  const hideHeaderScrollCondition = isScreenMd ? !isScrolledToTop : isUnderProductNav
  const isHeaderHidden = useHideHeaderOnScrollCondition(hideHeaderScrollCondition, hideAmount)

  // Style menu item when respective attribute panel is active
  // Includes scroll animation logic for mobile view
  const activeAttributePanel = useActiveAttributePanel({
    ...navigationRefs,
    navbarScrollableRef,
    navbarRef,
    isHeaderHidden,
  })

  // Dynamic styling values to ensure responsiveness
  const [scrollingUpStyle, setScrollingUpStyle] = useState<CSSProperties>({})

  const getScrollOffset = (targetRef?: RefObject<HTMLDivElement>) => {
    if (targetRef?.current && isMounted) {
      const targetTop = getRefDimension('top', targetRef) + scrollPosition
      const isTargetUnderNav = navbarBottom < targetTop
      const headerOffset = isTargetUnderNav ? 0 : headerHeight

      // Used to check if additional margin is needed for positioning
      const isAttributeSegment = targetRef !== supplierProductsRef

      // -1px to ensure link is highlighted
      const marginOffset = isAttributeSegment ? SEGMENT_MARGIN - 1 : -1

      return navbarHeight + headerOffset + marginOffset
    }
    return 0
  }

  const highlightAttributeLinkClassName = (panel: string) => (
    activeAttributePanel === panel ? 'active' : ''
  )

  // Set product navigation position dynamically
  useEffect(() => {
    setScrollingUpStyle({
      transform: `translate3D(0,${headerHeight}px,0)`,
    })
  }, [mountedWindow?.innerWidth, isScrollingUp])

  // If navbar isn't scrollable, hide both navigation arrows
  useEffect(() => {
    if (navbarScrollableElement
      && !(navbarScrollableElement?.scrollWidth > navbarScrollableElement?.clientWidth)) {
      setIsRightArrowVisible(true)
      setIsLeftArrowVisible(false)
    }
  }, [navbarScrollableElement?.scrollWidth])

  // Show/hide mobile arrow navigation buttons
  useEffect(() => {
    // Ensure navbar is scrolled to the left on page load
    if (navbarScrollableElement) navbarScrollableElement.scrollLeft = 0

    const handleNavScroll = debounce(() => {
      // 5px buffer for hiding arrows
      const isLeftVisible = !(navbarScrollableElement && navbarScrollableElement.scrollLeft <= 5)
      const isRightVisible = !(navbarScrollableElement
        && navbarScrollableElement.scrollLeft
        >= navbarScrollableElement.scrollWidth - navbarScrollableElement.clientWidth - 5)

      setIsLeftArrowVisible(isLeftVisible)
      setIsRightArrowVisible(isRightVisible)
    }, 300)

    navbarScrollableElement?.addEventListener('scroll', handleNavScroll)

    return () => {
      navbarScrollableElement?.removeEventListener('scroll', handleNavScroll)
    }
  }, [navbarScrollableElement === null])

  const onArrowClick = (direction: 'right' | 'left') => {
    if (!navbarScrollableElement) return

    const scrolled = navbarScrollableElement.scrollLeft
    const scrollAmount = 100
    const scrollTo = direction === 'right' ? scrollAmount : -(scrollAmount)

    navbarScrollableElement.scrollTo({
      top: 0,
      left: scrolled + scrollTo,
      behavior: 'smooth',
    })
  }

  return (
    <>
      <div
        className={`
          ${styles['header-background']}
          ${isScrollingUp && isUnderProductNav ? styles['scrolling-up'] : ''}`}
        style={isScrollingUp && isUnderProductNav ? scrollingUpStyle : {}}
      />
      <Navbar
        ref={navbarRef}
        className={`
          ${styles['single-product-nav']}
          ${isUnderProductNav ? styles['sticky-nav'] : ''}
          ${isScrollingUp && isUnderProductNav ? styles['scrolling-up'] : styles['scrolling-down']}`}
        style={
          isScrollingUp && isUnderProductNav ? scrollingUpStyle : {}
        }
        appearance="subtle"
      >
        <Nav
          as="ul"
          ref={navbarScrollableRef}
        >
          <Nav.Item
            as="li"
            onClick={() => scrollToRef(productFeaturesRef, getScrollOffset(productFeaturesRef))}
            className={highlightAttributeLinkClassName('features')}
          >
            {t('Features')}
          </Nav.Item>
          <Nav.Item
            as="li"
            onClick={() => scrollToRef(productStandardsRef, getScrollOffset(productStandardsRef))}
            className={highlightAttributeLinkClassName('standards')}
          >
            {t('Standards')}
          </Nav.Item>
          <Nav.Item
            as="li"
            onClick={() => scrollToRef(deliveryTermsRef, getScrollOffset(deliveryTermsRef))}
            className={highlightAttributeLinkClassName('deliveryTerms')}
          >
            {t('Delivery terms')}
          </Nav.Item>
          <Nav.Item
            as="li"
            onClick={() => scrollToRef(supplierProductsRef, getScrollOffset(supplierProductsRef))}
            className={highlightAttributeLinkClassName('supplierCard')}
          >
            {t("Supplier's other products")}
          </Nav.Item>
        </Nav>
        {(isScreenSm || isScreenLg) && (
          // Horizontal scroll buttons
          // Hidden on Md, as navbar isn't scrollable on those screen sizes
          <>
            <Button
              className={`${styles['nav-scroll-button']} ${styles.left} ${!isLeftArrowVisible ? styles.hidden : ''}`}
              appearance="subtle"
              onClick={() => onArrowClick('left')}
            >
              <LeftCarouselIcon />
            </Button>
            <Button
              className={`${styles['nav-scroll-button']} ${styles.right} ${!isRightArrowVisible ? styles.hidden : ''}`}
              appearance="subtle"
              onClick={() => onArrowClick('right')}
            >
              <RightCarouselIcon />
            </Button>
          </>
        )}
      </Navbar>
      {isUnderProductNav
      && (<div style={{ position: 'relative', height: `${navbarHeight + NAVBAR_TOP_MARGIN}px` }} />)}
    </>
  )
}

export default ProductNavigation
